import React, { useCallback, useEffect, useState } from "react";
import moment from "moment/moment";
import { CalendarDays, FileText, Maximize2 } from "lucide-react";
import { useLocation, useSearchParams } from "react-router-dom";
import { API_HOST_WMS } from "config/api.config";
import { api } from "network/network";
import { APIendpoints } from "utils/constants/APIendpoints";
import { STATUS_BEGIN, STATUS_DONE, STATUS_FAIL } from "utils/constants/others";
import {
  formatInvoiceAmount,
  formatWeight,
  formatWeightValue,
} from "./planShipmentUtils";
import {
  formatShippingNoteSlot,
  getShippingNoteDocument,
} from "./planShipmentShippingNotesUtils";
import {
  DetailPageFrame,
  DetailPageShell,
  DetailScrollArea,
} from "./styles.scheduleGroupDetails";
import {
  EmptyState,
  ErrorMessage,
  RetryButton,
  SkeletonCard,
  SkeletonLine,
  StateTitle,
} from "./styles.planShipment";
import {
  ASNDetailContent,
  ASNSectionTitle,
  DeliveryScheduleCard,
  DocumentViewButton,
  ScheduleCalendar,
  ScheduleCopy,
  ShipmentSummaryCard,
  ShipmentSummaryRows,
} from "./styles.advanceShippingNoteDetails";
import {
  InvoiceCard,
  InvoiceCardExpandButton,
  InvoiceCardList,
  MarkEntryHeader,
  MarkEntryHeaderTitle,
  PublicPageState,
  ShipmentSummaryDivider,
  ShipmentSummaryHeader,
  SwipeConfirmBar,
  SwipeErrorText,
} from "./styles.publicMarkEntry";
import SwipeToMarkEntry from "./SwipeToMarkEntry";
import MarkArrivedOutcome from "./MarkArrivedOutcome";
import { getErrorMessage, isMarkArrivedOutcome, useMarkArrived } from "./useMarkArrived";

const formatCount = (value) => formatWeightValue(value) || "0";

const formatDetailDate = (value) => {
  const date = moment(value);
  return value && date.isValid() ? date.format("D MMM YYYY") : "—";
};

const getDeliveryTypeLabel = (deliveryType) => {
  const normalizedDeliveryType = String(deliveryType || "").toLowerCase();
  if (normalizedDeliveryType === "courier") return "Courier / Part load";
  if (normalizedDeliveryType === "direct") return "Direct Vehicle";
  return "—";
};

const resolveDocumentUrl = (documentPath) => {
  try {
    return new URL(documentPath, window.location.origin).href;
  } catch (_) {
    return documentPath;
  }
};

const MarkEntryDetailSkeleton = () => (
  <ASNDetailContent aria-label="Loading shipment details">
    <SkeletonCard>
      <SkeletonLine $height="72px" />
    </SkeletonCard>
    <SkeletonLine $width="44%" $height="18px" />
    <SkeletonCard>
      {[0, 1, 2, 3, 4, 5].map((item) => (
        <SkeletonLine key={item} $height="16px" />
      ))}
    </SkeletonCard>
    <SkeletonLine $width="58%" $height="18px" />
    <SkeletonCard>
      <SkeletonLine $height="58px" />
      <SkeletonLine $height="58px" />
    </SkeletonCard>
  </ASNDetailContent>
);

const PublicMarkEntry = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const encryptedCode = (searchParams.get("encrypted_code") || "").trim();
  const uniqueCode = (searchParams.get("code") || "").trim();
  const hasLinkParam = Boolean(encryptedCode || uniqueCode);
  // Vehicle number entered on the QR gate page (PublicMarkEntryQr), carried forward via
  // router state since the shared/generic code's ASN response often has no vehicle number.
  const manualVehicleNumber = (location.state?.vehicleNumber || "").trim();

  const [detailStatus, setDetailStatus] = useState(STATUS_BEGIN);
  const [asnInfo, setAsnInfo] = useState(null);
  const [detailError, setDetailError] = useState("");
  const [isAlreadyArrived, setIsAlreadyArrived] = useState(false);

  const {
    status: markArrivedStatus,
    error: markArrivedError,
    radiusInMeters,
    confirm: confirmMarkArrived,
  } = useMarkArrived();

  const fetchAsnDetails = useCallback(() => {
    if (!hasLinkParam) {
      setDetailStatus(STATUS_FAIL);
      setDetailError("This link is invalid or has expired.");
      return;
    }

    setDetailStatus(STATUS_BEGIN);
    setDetailError("");

    api
      .get(API_HOST_WMS, APIendpoints.markPlanShipmentEntry, {
        query: {
          ...(encryptedCode ? { encrypted_code: encryptedCode } : {}),
          ...(uniqueCode ? { code: uniqueCode } : {}),
        },
      })
      .then((response) => {
        const responseData = response?.data?.response?.asn_info || null;
        if (!responseData || !(Number(responseData.asn_id) > 0)) {
          throw new Error("This link is invalid or has expired.");
        }
        setAsnInfo(responseData);
        setDetailStatus(STATUS_DONE);
        // If ASN is already ARRIVED, show success screen directly
        const isArrived = String(responseData.status || "").toUpperCase() === "ARRIVED";
        setIsAlreadyArrived(isArrived);
      })
      .catch((error) => {
        setAsnInfo(null);
        setDetailStatus(STATUS_FAIL);
        setDetailError(
          getErrorMessage(error, "This link is invalid or has expired.")
        );
      });
  }, [encryptedCode, hasLinkParam, uniqueCode]);

  useEffect(() => {
    fetchAsnDetails();
  }, [fetchAsnDetails]);

  const openDocument = useCallback((documentPath, title) => {
    if (!documentPath) return;
    const documentUrl = resolveDocumentUrl(documentPath);

    if (
      window.localStorage.getItem("referrer_platform") === "app" &&
      window.openDocumentHandler
    ) {
      window.openDocumentHandler.postMessage(
        JSON.stringify({ url: documentUrl, title, showDownload: false })
      );
      return;
    }

    window.open(documentUrl, "_blank", "noopener,noreferrer");
  }, []);

  const confirmMarkEntry = useCallback(() => {
    confirmMarkArrived({
      encryptedCode,
      code: uniqueCode,
      vehicleNumber: manualVehicleNumber || asnInfo?.delivery_details?.vehicle_number || "",
    });
  }, [asnInfo, confirmMarkArrived, encryptedCode, manualVehicleNumber, uniqueCode]);

  const deliveryDetails = asnInfo?.delivery_details || {};
  const isCourier = String(deliveryDetails.delivery_type || "").toLowerCase() === "courier";
  const ewayBillDocument = getShippingNoteDocument(asnInfo || {}, "eway");
  const courierDocument = getShippingNoteDocument(asnInfo || {}, "courier");
  const purchaseOrders = Array.isArray(asnInfo?.purchase_orders)
    ? asnInfo.purchase_orders
    : [];
  const invoices = purchaseOrders.flatMap((purchaseOrder) =>
    Array.isArray(purchaseOrder?.invoices) ? purchaseOrder.invoices : []
  );

  return (
    <DetailPageShell>
      <DetailPageFrame>
        <MarkEntryHeader>
          <MarkEntryHeaderTitle>Mark entry to warehouse</MarkEntryHeaderTitle>
        </MarkEntryHeader>

        <DetailScrollArea>
          {isMarkArrivedOutcome(markArrivedStatus) ? (
            <MarkArrivedOutcome
              status={markArrivedStatus}
              onRetry={confirmMarkEntry}
              radiusInMeters={radiusInMeters}
            />
          ) : isAlreadyArrived ? (
            <MarkArrivedOutcome status={STATUS_DONE} onRetry={() => {}} radiusInMeters={null} />
          ) : (
            <>
              {detailStatus === STATUS_BEGIN ? <MarkEntryDetailSkeleton /> : null}

              {detailStatus === STATUS_FAIL ? (
                <PublicPageState>
                  <EmptyState>
                    <StateTitle>Unable to load this link</StateTitle>
                    <ErrorMessage>{detailError}</ErrorMessage>
                    {hasLinkParam ? (
                      <RetryButton type="button" onClick={fetchAsnDetails}>
                        Try again
                      </RetryButton>
                    ) : null}
                  </EmptyState>
                </PublicPageState>
              ) : null}

              {detailStatus === STATUS_DONE && asnInfo ? (
                <ASNDetailContent>
                  <DeliveryScheduleCard>
                    <ScheduleCalendar>
                      <CalendarDays aria-hidden="true" />
                    </ScheduleCalendar>
                    <ScheduleCopy>
                      <strong>
                        {formatDetailDate(asnInfo.delivery_date)}
                        {" • "}
                        {formatShippingNoteSlot(asnInfo.slot)}
                      </strong>
                      <span>
                        {asnInfo.warehouse?.warehouse_name ||
                          asnInfo.warehouse?.warehouse_code ||
                          "—"}
                      </span>
                    </ScheduleCopy>
                  </DeliveryScheduleCard>

                  <ASNSectionTitle>
                    <span>Shipment summary</span>
                  </ASNSectionTitle>
                  <ShipmentSummaryCard>
                    <ShipmentSummaryHeader>
                      <strong>
                        {isCourier
                          ? deliveryDetails.da_name || "—"
                          : deliveryDetails.vehicle_number || manualVehicleNumber || "—"}
                      </strong>
                      {isCourier ? (
                        <span>
                          {courierDocument?.document_number || deliveryDetails.docket_number || "—"}
                        </span>
                      ) : (
                        <span>{`${deliveryDetails.da_name || "—"} (You)`}</span>
                      )}
                    </ShipmentSummaryHeader>
                    <ShipmentSummaryDivider />
                    <ShipmentSummaryRows>
                      <dt>ASN no.</dt>
                      <dd>{asnInfo.asn_number || "—"}</dd>
                      <dt>Invoices</dt>
                      <dd>{formatCount(asnInfo.invoice_count)}</dd>
                      <dt>SKUs</dt>
                      <dd>{formatCount(asnInfo.total_sku_count)}</dd>
                      <dt>Net weight</dt>
                      <dd>{formatWeight(asnInfo.total_weight_in_kg)}</dd>
                      <dt>Total amount</dt>
                      <dd>{formatInvoiceAmount(asnInfo.total_amount)}</dd>
                      <dt>Delivery type</dt>
                      <dd>{getDeliveryTypeLabel(deliveryDetails.delivery_type)}</dd>
                      {isCourier ? (
                        <>
                          <dt>Docket receipt</dt>
                          <dd>
                            {courierDocument?.document_path ? (
                              <DocumentViewButton
                                type="button"
                                onClick={() =>
                                  openDocument(courierDocument.document_path, "Docket receipt")
                                }
                              >
                                View receipt
                                <FileText aria-hidden="true" />
                              </DocumentViewButton>
                            ) : (
                              "—"
                            )}
                          </dd>
                        </>
                      ) : null}
                      {ewayBillDocument?.document_path || ewayBillDocument?.document_number ? (
                        <>
                          <dt>E way bill</dt>
                          <dd>
                            {ewayBillDocument?.document_path ? (
                              <DocumentViewButton
                                type="button"
                                onClick={() =>
                                  openDocument(ewayBillDocument.document_path, "E way bill")
                                }
                              >
                                View bill
                                <FileText aria-hidden="true" />
                              </DocumentViewButton>
                            ) : (
                              "—"
                            )}
                          </dd>
                          <dt>E way bill no.</dt>
                          <dd>{ewayBillDocument?.document_number || "—"}</dd>
                        </>
                      ) : null}
                    </ShipmentSummaryRows>
                  </ShipmentSummaryCard>

                  <ASNSectionTitle>
                    <span>Invoices in this shipment</span>
                  </ASNSectionTitle>
                  {invoices.length > 0 ? (
                    <InvoiceCardList>
                      {invoices.map((invoice, index) => (
                        <InvoiceCard
                          key={invoice.vendor_invoice_id || `${invoice.invoice_number}-${index}`}
                        >
                          <ScheduleCalendar>
                            <FileText aria-hidden="true" />
                          </ScheduleCalendar>
                          <ScheduleCopy>
                            <strong>{invoice.invoice_number || "—"}</strong>
                            <span>
                              {formatCount(invoice.number_of_skus)} SKUs
                              {" • "}
                              {formatWeight(invoice.weight_in_kg)}
                              {" • "}
                              {formatInvoiceAmount(invoice.invoice_amount)}
                            </span>
                          </ScheduleCopy>
                          {invoice.invoice_path ? (
                            <InvoiceCardExpandButton
                              type="button"
                              onClick={() => openDocument(invoice.invoice_path, invoice.invoice_number)}
                              aria-label={`Open invoice ${invoice.invoice_number || ""}`}
                            >
                              <Maximize2 aria-hidden="true" />
                            </InvoiceCardExpandButton>
                          ) : null}
                        </InvoiceCard>
                      ))}
                    </InvoiceCardList>
                  ) : (
                    <EmptyState>
                      <StateTitle>No invoices found</StateTitle>
                      <span>This shipment has no mapped invoices.</span>
                    </EmptyState>
                  )}
                </ASNDetailContent>
              ) : null}
            </>
          )}
        </DetailScrollArea>

        {detailStatus === STATUS_DONE &&
        asnInfo &&
        !isMarkArrivedOutcome(markArrivedStatus) &&
        !isAlreadyArrived ? (
          <SwipeConfirmBar>
            {markArrivedError ? <SwipeErrorText>{markArrivedError}</SwipeErrorText> : null}
            <SwipeToMarkEntry
              label="Swipe to mark entry"
              status={markArrivedStatus}
              onConfirm={confirmMarkEntry}
            />
          </SwipeConfirmBar>
        ) : null}
      </DetailPageFrame>
    </DetailPageShell>
  );
};

export default PublicMarkEntry;
